/// <reference types="vite/client" />
/**
 * Environment Configuration Manager
 * Validates and provides type-safe access to environment variables
 */

interface EnvironmentConfig {
  // Application Settings
  appEnv: 'development' | 'staging' | 'production';
  appName: string;
  apiBaseUrl: string;

  // Feature Flags
  enableAIAnalysis: boolean;
  enableSecurityHooks: boolean;
  useFallbackData: boolean;
  /**
   * When true (default: production builds): investor demo sessions, client-side demo data
   * bypasses, and revenue placeholders are disabled — only real API / auth paths apply.
   */
  sovereignLiveOnly: boolean;
  /**
   * When true: show free-trial / Citizen promo CTAs. Default false — paid sovereign tiers only.
   * Must match backend ANCHOR_PROMO_ACTIVE for billing behaviour.
   */
  promoActive: boolean;
  /**
   * Internal-only escape hatch. Default false so rough generated TAZZIE videos are not treated as
   * public-ready entertainment.
   */
  allowGeneratedTazzieVideo: boolean;

  // Security Settings
  rateLimitRpm: number;
  sessionTimeout: number;
  enableCSP: boolean;

  // Monitoring
  sentryDsn?: string;
  gaId?: string;
  logRocketId?: string;

  // Google Maps / Earth API
  googleMapsApiKey?: string;

  // Cesium Ion (3D sovereign globe)
  cesiumIonToken?: string;

  // App version (for Sentry release tracking)
  appVersion: string;

  // WebSocket URL for live map presence / telemetry
  wsUrl?: string;

  // Performance
  enableServiceWorker: boolean;
  cacheDuration: number;

  // Development
  debugMode: boolean;
  enableDevTools: boolean;
}

/**
 * Parse boolean from environment variable
 */
function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true';
}

/**
 * Parse number from environment variable
 */
function parseNumber(value: string | undefined, defaultValue: number): number {
  if (!value) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Validate required environment variables
 */
function validateEnvironment(): void {
  const errors: string[] = [];

  // In production, certain variables are required
  if (import.meta.env.VITE_APP_ENV === 'production') {
    // Note: GEMINI_API_KEY is handled by the backend for security reasons
    // Frontend should not have direct access to API keys
    if (import.meta.env.VITE_USE_FALLBACK_DATA === 'true') {
      errors.push('VITE_USE_FALLBACK_DATA must be false in production');
    }
    // API URL must be set in production - no localhost fallbacks allowed
    if (!import.meta.env.VITE_API_BASE_URL) {
      errors.push('VITE_API_BASE_URL must be set in production');
    }
    const apiUrl = import.meta.env.VITE_API_BASE_URL || '';
    if (
      apiUrl &&
      /localhost|127\.0\.0\.1/.test(apiUrl) &&
      import.meta.env.VITE_ALLOW_LOCAL_API !== 'true'
    ) {
      errors.push(
        'VITE_API_BASE_URL must point to your deployed API in production (not localhost). Same-origin /api is OK. Set VITE_ALLOW_LOCAL_API=true only for rare debug builds.'
      );
    }
  }

  if (errors.length > 0) {
    throw new Error(`Invalid environment configuration:\n- ${errors.join('\n- ')}`);
  }
}

/**
 * Load and validate environment configuration
 */
export function loadEnvironmentConfig(): EnvironmentConfig {
  // Validate environment first
  if (import.meta.env.PROD) {
    validateEnvironment();
  }

  const config: EnvironmentConfig = {
    // Application Settings
    appEnv: (import.meta.env.VITE_APP_ENV || 'development') as EnvironmentConfig['appEnv'],
    appName: import.meta.env.VITE_APP_NAME || 'Anchor AI Guard',
    // Default to same-origin `/api` so local Vite dev uses the proxy and production uses the reverse proxy.
    // Override with VITE_API_BASE_URL only when a different API origin is intentional.
    apiBaseUrl:
      import.meta.env.VITE_API_BASE_URL ||
      (import.meta.env.DEV ? 'http://127.0.0.1:3001/api' : '/api'),

    // Feature Flags
    enableAIAnalysis: parseBoolean(import.meta.env.VITE_ENABLE_AI_ANALYSIS, true),
    enableSecurityHooks: parseBoolean(import.meta.env.VITE_ENABLE_SECURITY_HOOKS, true),
    // Default to live data; enable fallback data explicitly via VITE_USE_FALLBACK_DATA=true
    useFallbackData: parseBoolean(import.meta.env.VITE_USE_FALLBACK_DATA, false),
    // Production builds default to sovereign live mode (no toy demo). Set VITE_SOVEREIGN_LIVE_ONLY=false only for emergency demos on a prod URL.
    sovereignLiveOnly: parseBoolean(import.meta.env.VITE_SOVEREIGN_LIVE_ONLY, import.meta.env.PROD),
    promoActive: parseBoolean(import.meta.env.VITE_ANCHOR_PROMO_ACTIVE, false),
    allowGeneratedTazzieVideo: parseBoolean(
      import.meta.env.VITE_TAZZIE_ALLOW_GENERATED_VIDEO,
      true
    ),

    // Security Settings
    rateLimitRpm: parseNumber(import.meta.env.VITE_RATE_LIMIT_RPM, 60),
    sessionTimeout: parseNumber(import.meta.env.VITE_SESSION_TIMEOUT, 30),
    enableCSP: parseBoolean(import.meta.env.VITE_ENABLE_CSP, true),

    // Google Maps / Earth API
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,

    // Cesium Ion (3D sovereign globe)
    cesiumIonToken: import.meta.env.VITE_CESIUM_ION_TOKEN,

    // App version
    appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // WebSocket URL for live map presence
    wsUrl: import.meta.env.VITE_WS_URL,

    // Monitoring
    sentryDsn: import.meta.env.VITE_SENTRY_DSN,
    gaId: import.meta.env.VITE_GA_ID,
    logRocketId: import.meta.env.VITE_LOGROCKET_ID,

    // Performance
    enableServiceWorker: parseBoolean(import.meta.env.VITE_ENABLE_SERVICE_WORKER, false),
    cacheDuration: parseNumber(import.meta.env.VITE_CACHE_DURATION, 3600),

    // Development
    debugMode: parseBoolean(import.meta.env.VITE_DEBUG_MODE, false),
    enableDevTools: parseBoolean(import.meta.env.VITE_ENABLE_DEVTOOLS, true),
  };

  // Log non-sensitive config in development only
  if (config.debugMode && config.appEnv === 'development') {
    console.debug('[env] Loaded environment config', {
      appEnv: config.appEnv,
      appName: config.appName,
      apiBaseUrl: config.apiBaseUrl,
      enableAIAnalysis: config.enableAIAnalysis,
      enableSecurityHooks: config.enableSecurityHooks,
      useFallbackData: config.useFallbackData,
      sovereignLiveOnly: config.sovereignLiveOnly,
      promoActive: config.promoActive,
      allowGeneratedTazzieVideo: config.allowGeneratedTazzieVideo,
      rateLimitRpm: config.rateLimitRpm,
      sessionTimeout: config.sessionTimeout,
      enableCSP: config.enableCSP,
      appVersion: config.appVersion,
      enableServiceWorker: config.enableServiceWorker,
      cacheDuration: config.cacheDuration,
      debugMode: config.debugMode,
      enableDevTools: config.enableDevTools,
    });
  }

  return config;
}

// Export singleton instance
export const env = loadEnvironmentConfig();

// Export helper functions
export function isDevelopment(): boolean {
  return env.appEnv === 'development';
}

export function isProduction(): boolean {
  return env.appEnv === 'production';
}

export function isStaging(): boolean {
  return env.appEnv === 'staging';
}

/** True when demo sessions and client-side placeholder data are disabled (see env.sovereignLiveOnly). */
export function isSovereignLiveOnly(): boolean {
  return env.sovereignLiveOnly;
}
